import React, { useEffect } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import styled, { createGlobalStyle } from 'styled-components'
import { identity, isEmpty, pathOr } from 'ramda'
import isEqual from 'lodash/isEqual'

import { colors } from '@/styles'
import { useDispatch, useSelector } from '@/lib/redux'
import { createSelector } from '@/utils'
import handleSession from '@/utils/handle-session'
import useCurrentSession from '@/hooks/use-current-session'
import { show as showLoginModal } from '@/features/Auth/login-modal'
import Footer, { FooterProps } from '@/features/Footer'
import Header, { HeaderProps, HeaderTheme } from '@/features/Header'
import { useCheckRecentMessages } from '@/features/Listings/hooks/recent-messages'
import CookieBanner from '@/components/cookie-banner'
import { PromotionBanner } from '@/components/promotions/promotion-banner'
import SupportOverlay from '@/components/support-overlay'
import { TermsDialogProvider } from '@/components/terms-dialog/context'
import useEventListener from '@/components/use-event-listener'

import { DSLoginProvider } from '../Auth/Context/ds-login-provider'

const Container = styled.div<{ background?: string }>`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: ${props => props.background || colors.white};
`

const DisableZoom = createGlobalStyle`
  html, body {
    touch-action: pan-x pan-y;
  }
`

const selectFavourites = createSelector(
  pathOr({}, ['listings', 'favourites']),
  identity,
  { memoizeOptions: { equalityCheck: isEqual } }
)

export interface MainContainerProps {
  children?: React.ReactNode
  headerConfig?: HeaderProps
  footerConfig?: FooterProps
  supportConfig?: {}
  disableZoom?: boolean
  suppressRegisterModal?: boolean
  background?: string
}

const MainContainer: React.FC<MainContainerProps> = ({
  children,
  headerConfig = {
    theme: HeaderTheme.DEFAULT
  },
  footerConfig = {},
  supportConfig = {},
  disableZoom = false,
  suppressRegisterModal,
  ...rest
}) => {
  const dispatch = useDispatch()
  const router = useRouter()
  const session = useCurrentSession()
  const hasSession = !!session
  const { t } = useTranslation()

  const favourites = useSelector(selectFavourites)
  useCheckRecentMessages()

  useEffect(() => {
    if (hasSession && session.uuid) {
      dispatch.sse.subscribe(session.uuid)
    }
    if (session?.registration_complete === '0' && !suppressRegisterModal) {
      showLoginModal({
        mode: 'login',
        t
      })
    }
    return () => dispatch.sse.unsubscribe()
  }, [
    dispatch.sse,
    session.uuid,
    session?.registration_complete,
    suppressRegisterModal,
    t,
    hasSession
  ])

  useEffect(() => {
    if (hasSession && session.user_type === 'tenant' && isEmpty(favourites)) {
      dispatch.listings.getFavourites()
    }
  }, [hasSession, session.user_type, favourites, dispatch.listings])

  useEventListener('storage', (e: any) => {
    if (e.key === 'session-id') {
      if (!!e.oldValue && e.oldValue !== e.newValue) {
        dispatch.session.clear()
      } else if (!e.oldValue && !!e.newValue) {
        handleSession(dispatch.session.loadSession())
      }
    }
  })

  return (
    <Container {...rest}>
      {disableZoom && <DisableZoom />}
      <SupportOverlay {...supportConfig} />
      <Header banner={<PromotionBanner />} {...headerConfig} />
      <DSLoginProvider>
        <TermsDialogProvider>{children}</TermsDialogProvider>
      </DSLoginProvider>
      <CookieBanner lang={router.locale} privacyPolicyUrl={'/privacy'} />
      <Footer {...footerConfig} />
    </Container>
  )
}

export default MainContainer
